import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import SimpleGrid from '~/common/components/Grids/Grid/SimpleGrid';
import Text from '~/common/components/Texts/Text';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import { formatCurrency } from '~/common/util/format';
import { FormTypes } from '~/pages/IncomeCalculator/enums';
import type { CalculationResult } from '~/pages/IncomeCalculator/types';

import BreakdownContainer from '../BreakdownContainer';
import lang from '../language.json';
import BreakdownValueWithChild from '../styles/BreakdownValueWithChild';
import BreakdownYearContainer from '../styles/BreakdownYearContainer';
import TrendingAnalysis from './TrendingAnalysis';

const k1CombineBox = (
  selectedResult: CalculationResult,
  currentYear: number,
) => {
  return (
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1,
      currentYear,
      'ordinaryBusinessIncome',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1,
      currentYear,
      'netRentalIncome',
    ) +
    selectedResult.getInputAsFloat(
      FormTypes.ScheduleK1,
      currentYear,
      'otherNetRentalIncome',
    )
  );
};

const totalAddbacksAndDeductions = (
  selectedResult: CalculationResult,
  currentYear: number,
  mortgageValue?: number,
  nonRecurringValue?: number,
) => {
  const mortgage =
    mortgageValue ??
    selectedResult.getInputAsFloat(
      FormTypes.Form1120S,
      currentYear,
      'mortgagesNotesBondsPayableInLessThanOneYear',
    ) ??
    0;

  const nonRecurring =
    nonRecurringValue ??
    selectedResult.getInputAsFloat(
      FormTypes.Form1120S,
      currentYear,
      'nonRecurringOtherIncome',
    ) ??
    0;
  return (
    (selectedResult.getInputAsFloat(
      FormTypes.Form1120S,
      currentYear,
      'depletion',
    ) || 0) +
    (selectedResult.getInputAsFloat(
      FormTypes.Form1120S,
      currentYear,
      'depreciation',
    ) || 0) +
    (selectedResult.getInputAsFloat(
      FormTypes.Form1120S,
      currentYear,
      'amortizationCasualtyLoss',
    ) || 0) -
    (nonRecurring +
      mortgage +
      (selectedResult.getInputAsFloat(
        FormTypes.Form1120S,
        currentYear,
        'travelAndEntertainment',
      ) || 0))
  );
};

const annualIncome = (
  selectedResult: CalculationResult,
  currentYear: number,
) => {
  return selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'annualIncome',
  );
};

const addSign = (v: number) => (v < 0 ? '-' : '+');
const subtractSign = (v: number) => (v < 0 ? '+' : '-');
const emptyOrMinus = (v: number) => (v < 0 ? '-' : '');

type BreakdownView1120sProps = {
  currentYear: number;
  showFullBreakdown: boolean;
  selectedResult: CalculationResult;
};

const BreakdownView1120s = ({
  currentYear,
  selectedResult,
  showFullBreakdown,
}: BreakdownView1120sProps) => {
  const useK1 =
    selectedResult
      .getInput(FormTypes.ScheduleK1, currentYear, 'utilizeK1Value')
      ?.fieldValue?.toLowerCase() === 'true';

  const productType = useGetLoanSummary().data?.loanInfo?.loanProductType;
  const isConventionalOrFHA =
    productType === 'Conventional' || productType === 'FHA';

  const k1Combined = k1CombineBox(selectedResult, currentYear);
  const distributions = selectedResult.getInputAsFloat(
    FormTypes.ScheduleK1,
    currentYear,
    'distributions',
  );
  const k1OrDistribution = useK1 ? k1Combined : distributions;
  const {
    FIELDS: { ['1120S']: FIELDS },
  } = lang;

  const medicareWages = selectedResult.getInputAsFloat(
    FormTypes.W2,
    currentYear,
    'medicareWages',
  );
  const ordinaryBusinessIncome = selectedResult.getInputAsFloat(
    FormTypes.ScheduleK1,
    currentYear,
    'ordinaryBusinessIncome',
  );
  const netRentalIncome = selectedResult.getInputAsFloat(
    FormTypes.ScheduleK1,
    currentYear,
    'netRentalIncome',
  );
  const otherNetRentalIncome = selectedResult.getInputAsFloat(
    FormTypes.ScheduleK1,
    currentYear,
    'otherNetRentalIncome',
  );
  const nonRecurringOtherIncome = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'nonRecurringOtherIncome',
  );
  const depletion = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'depletion',
  );
  const depreciation = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'depreciation',
  );
  const line1415 = depletion + depreciation;
  const mortgages = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'mortgagesNotesBondsPayableInLessThanOneYear',
  );
  const isMortgagesExcluded =
    isConventionalOrFHA &&
    selectedResult
      .getInput(
        FormTypes.Form1120S,
        currentYear,
        'excludeMortgagesNotesBondsPayableInLessThanOneYear',
      )
      ?.fieldValue?.toLowerCase() === 'true';
  const isNonRecurringExcluded =
    selectedResult
      .getInput(
        FormTypes.Form1120S,
        currentYear,
        'excludeNonRecurringOtherIncome',
      )
      ?.fieldValue?.toLowerCase() === 'true';
  const travelAndEntertainment = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'travelAndEntertainment',
  );
  const amortization = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'amortizationCasualtyLoss',
  );
  const percentageOwnership = selectedResult.getInputAsFloat(
    FormTypes.ScheduleK1,
    currentYear,
    'percentageOwnership',
  );
  const percentOwnershipDisplay = percentageOwnership * 100 || 0;
  const totalAddbacks = totalAddbacksAndDeductions(
    selectedResult,
    currentYear,
    isMortgagesExcluded ? 0 : mortgages,
    isNonRecurringExcluded ? 0 : nonRecurringOtherIncome,
  );
  const annual = annualIncome(selectedResult, currentYear);
  const subTotal = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'subTotal',
  );
  const borrowerShare = selectedResult.getInputAsFloat(
    FormTypes.Form1120S,
    currentYear,
    'borrowerShareOfIncomeOrLoss',
  );

  return (
    <BreakdownYearContainer title={currentYear}>
      <BreakdownValueWithChild
        name={FIELDS.WAGES}
        value={medicareWages}
        isCurrency={true}
        childrenData={[
          {
            name: FIELDS.W2BOX5,
            value: medicareWages,
            sign: addSign(medicareWages),
          },
        ]}
        fontWeight="bold"
      />
      {showFullBreakdown && (
        <>
          {useK1 ? (
            <BreakdownValueWithChild
              name={FIELDS.K1BOXES}
              value={k1Combined}
              isCurrency={true}
              childrenData={[
                {
                  name: FIELDS.KBOX1,
                  value: ordinaryBusinessIncome,
                  sign: addSign(ordinaryBusinessIncome),
                },
                {
                  name: FIELDS.KBOX2,
                  value: netRentalIncome,
                  sign: addSign(netRentalIncome),
                },
                {
                  name: FIELDS.KBOX3,
                  value: otherNetRentalIncome,
                  sign: addSign(otherNetRentalIncome),
                },
              ]}
              fontWeight="bold"
            />
          ) : (
            <BreakdownValueWithChild
              name={FIELDS.DISTRIBUTIONS}
              value={distributions}
              isCurrency={true}
              childrenData={[
                {
                  name: FIELDS.DISTRIBUTIONS16D,
                  value: distributions,
                  sign: addSign(distributions),
                },
              ]}
              fontWeight="bold"
            />
          )}
          <BreakdownValueWithChild
            name={FIELDS.ADDBACK_DEDUCTIONS}
            value={totalAddbacks}
            isCurrency={true}
            childrenData={[
              ...(isNonRecurringExcluded
                ? []
                : [
                    {
                      name: FIELDS.LINE5,
                      value: nonRecurringOtherIncome,
                      sign: subtractSign(nonRecurringOtherIncome),
                    },
                  ]),
              {
                name: FIELDS.LINE1415,
                value: line1415,
                sign: addSign(line1415),
              },
              ...(isMortgagesExcluded
                ? []
                : [
                    {
                      name: FIELDS.LLINE17,
                      value: mortgages,
                      sign: subtractSign(mortgages),
                    },
                  ]),
              {
                name: FIELDS.M1LINE3B,
                value: travelAndEntertainment,
                sign: subtractSign(travelAndEntertainment),
              },
              {
                name: FIELDS.AMORTIZATION,
                value: amortization,
                sign: addSign(amortization),
              },
            ]}
            fontWeight="bold"
          />
          <BreakdownValueWithChild
            name={FIELDS.PERCENTOWNERSHIP}
            value={percentOwnershipDisplay}
            isCurrency={false}
            childrenData={[
              {
                name: FIELDS.BOXF,
                value: percentOwnershipDisplay,
              },
            ]}
            fontWeight="bold"
          />
        </>
      )}
      {showFullBreakdown ? (
        <BreakdownValueWithChild
          name={lang.MONTHLY_CALC}
          isCurrency={true}
          childrenData={[
            {
              name: '',
              value: medicareWages,
              sign: addSign(medicareWages),
            },
            {
              name: '',
              value: k1OrDistribution,
              sign: addSign(k1OrDistribution),
            },
            {
              name: '',
              value: totalAddbacks,
              sign: addSign(totalAddbacks),
              monthlyIncome: {
                show: true,
                percentValue: percentageOwnership || 0,
                totalSumValue: annual,
              },
            },
          ]}
          fontWeight="bold"
        />
      ) : (
        <Box>
          <Flex fontWeight="bold">
            <Text fontSize="sm">{lang.MONTHLY_CALC}</Text>
          </Flex>
          <Text
            fontSize="sm"
            marginLeft="10px"
            marginTop="2px"
            style={{ textIndent: '-6px' }}
            pl={4}
          >
            {formatCurrency(
              selectedResult.getInput(
                FormTypes.W2,
                currentYear,
                'medicareWages',
              )?.fieldValue,
            )}
            &nbsp; &divide; 12 (Months)
          </Text>
        </Box>
      )}
      <BreakdownValueWithChild
        name={currentYear + ' Calculated Monthly Income'}
        isCurrency={true}
        childrenData={[
          {
            name: '',
            value: annual / 12,
            sign: emptyOrMinus(annual),
          },
        ]}
        fontWeight="bold"
      />
      {showFullBreakdown && (
        <>
          {' '}
          <BreakdownValueWithChild
            name={FIELDS.SCORP_SUBTOTAL}
            isCurrency={true}
            childrenData={[
              {
                name: '',
                value: subTotal,
                sign: emptyOrMinus(subTotal),
              },
            ]}
            fontWeight="bold"
          />
          <BreakdownValueWithChild
            name={FIELDS.BORROWER_SHARES_CORPINCOME}
            isCurrency={true}
            childrenData={[
              {
                name: '',
                value: borrowerShare,
                sign: emptyOrMinus(borrowerShare),
              },
            ]}
            fontWeight="bold"
          />
        </>
      )}
    </BreakdownYearContainer>
  );
};

type OneYearBreakdownProps = {
  currentYear: number;
  years: number[];
  selectedResult: CalculationResult;
};

const OneYearBreakdown = ({
  currentYear,
  years,
  selectedResult,
}: OneYearBreakdownProps) => {
  return (
    <SimpleGrid
      w={`${50 * Math.min(years.length, 2)}%`}
      columns={Math.min(2, years.length)}
      spacingX={0}
      spacingY={8}
      pb={4}
    >
      <BreakdownView1120s
        currentYear={currentYear}
        selectedResult={selectedResult}
        showFullBreakdown
      />
    </SimpleGrid>
  );
};

type TwoYearBreakdownProps = {
  currentYear: number;
  priorYear: number;
  years: number[];
  isPositiveTrend: string | undefined;
  formulaId: number | null;
  selectedResult: CalculationResult;
};

const TwoYearBreakdown = ({
  currentYear,
  priorYear,
  years,
  isPositiveTrend,
  formulaId,
  selectedResult,
}: TwoYearBreakdownProps) => {
  return (
    <>
      {isPositiveTrend === 'true' ? (
        <SimpleGrid
          w={`${50 * Math.min(years.length, 2)}%`}
          columns={Math.min(2, years.length)}
          spacingX={0}
          spacingY={8}
          pb={4}
        >
          <Box pr={4}>
            <BreakdownView1120s
              currentYear={currentYear}
              selectedResult={selectedResult}
              showFullBreakdown={true}
            />
          </Box>
          <BreakdownView1120s
            currentYear={priorYear}
            selectedResult={selectedResult}
            showFullBreakdown={true}
          />
        </SimpleGrid>
      ) : (
        <>
          {(formulaId === 32 || formulaId === 33) && (
            <SimpleGrid
              w={`${50 * Math.min(years.length, 2)}%`}
              columns={Math.min(2, years.length)}
              spacingX={0}
              spacingY={8}
              pb={4}
            >
              <Box pr={4}>
                <BreakdownView1120s
                  currentYear={currentYear}
                  selectedResult={selectedResult}
                  showFullBreakdown={false}
                />
              </Box>
              <BreakdownView1120s
                currentYear={priorYear}
                selectedResult={selectedResult}
                showFullBreakdown={false}
              />
            </SimpleGrid>
          )}

          {(formulaId === 34 || formulaId === 104 || formulaId === 105) && (
            <SimpleGrid
              w={`${50 * Math.min(years.length, 2)}%`}
              columns={Math.min(2, years.length)}
              spacingX={0}
              spacingY={8}
              pb={4}
            >
              <Box pr={4}>
                <BreakdownView1120s
                  currentYear={currentYear}
                  selectedResult={selectedResult}
                  showFullBreakdown={true}
                />
              </Box>
              <BreakdownView1120s
                currentYear={priorYear}
                selectedResult={selectedResult}
                showFullBreakdown={true}
              />
            </SimpleGrid>
          )}
        </>
      )}
    </>
  );
};

type Form1120sCalculationBreakdownProps = {
  selectedResult: CalculationResult;
  onNewWindow?: () => void;
  hideAccordion?: boolean;
};

const Form1120sCalculationBreakdown = ({
  selectedResult,
  onNewWindow,
  hideAccordion,
}: Form1120sCalculationBreakdownProps) => {
  const years = selectedResult
    .getFiscalYears()
    .filter((year) => year !== 0)
    .sort((a, b) => b - a);

  const currentYear = years[0];

  const isPositiveTrend = selectedResult
    .getInput(FormTypes.Form1120SOutput, 0, 'isPositiveTrend')
    ?.fieldValue?.toLowerCase();

  const formula = selectedResult.formulas[0];

  return (
    <BreakdownContainer
      header="1120S Calculation Breakdown"
      onNewWindow={onNewWindow}
      hideAccordion={hideAccordion}
    >
      <Box>
        {formula && (
          <Text pb={4} fontSize="sm">
            <Text as="span" fontWeight="bold">
              Monthly Income Equation:&nbsp;
            </Text>
            {formula.formula}
          </Text>
        )}

        {years.length === 1 && (
          <OneYearBreakdown
            currentYear={currentYear}
            years={years}
            selectedResult={selectedResult}
          />
        )}

        {years.length >= 2 && (
          <Box>
            <TwoYearBreakdown
              currentYear={currentYear}
              priorYear={years[1]}
              years={years}
              isPositiveTrend={isPositiveTrend}
              formulaId={formula?.formulaId ?? null}
              selectedResult={selectedResult}
            />
            <TrendingAnalysis selectedResult={selectedResult} />
          </Box>
        )}
      </Box>

      <Text mt={4}>
        <Text as="span" fontWeight={500} fontSize="lg">
          Calculated Monthly Income:{' '}
        </Text>
        {formatCurrency(selectedResult.calculationAmount)}

        {years.length >= 2 && !!isPositiveTrend && (
          <>
            &nbsp;&#40;
            {isPositiveTrend === 'true' ? 'Positive Trend' : 'Negative Trend'}
            &#41;
          </>
        )}
      </Text>
    </BreakdownContainer>
  );
};

export default Form1120sCalculationBreakdown;
